import React from 'react';
import { Brain, Zap, Target, BookOpen } from 'lucide-react';

// Content sourced from www.rudvan.online's "What is RUDVAN?" section
// (2026-08-07, per user request to bring that page's marketing copy into
// this app's landing page). The acronym breakdown and the four side cards
// are copied close to verbatim; only trimmed where it duplicated text
// already on this page (e.g. the hero photo already carries the
// "Adapt. Practice. Master." line).
const ACRONYM = [
  { letter: 'R', word: 'Reinforcement', description: 'Learns from every attempt you make' },
  { letter: 'U', word: 'Unified', description: 'Single platform, complete learning' },
  { letter: 'D', word: 'Dynamic', description: 'Adapts in real-time to your level' },
  { letter: 'V', word: 'Virtual', description: 'AI-powered guidance & tutoring' },
  { letter: 'A', word: 'Adaptive', description: 'Personalizes the learning journey' },
  { letter: 'N', word: 'Network', description: 'Powered by deep learning networks' },
];

const HIGHLIGHT_CARDS = [
  {
    icon: Brain,
    title: 'Understands You',
    description: 'Analyzes your learning patterns, identifies weak areas, and knows exactly what you need to practice next.',
  },
  {
    icon: Zap,
    title: 'Optimizes Learning',
    description: 'Dynamically adjusts difficulty, paces your learning, and reinforces concepts until they stick.',
  },
  {
    icon: Target,
    title: 'Guarantees Results',
    description: 'Built to improve scores meaningfully while reducing total study time, by focusing practice on what actually needs work.',
  },
  {
    icon: BookOpen,
    title: 'Our Vision',
    description: '"Adapt. Practice. Master." — automatically guide learners to top performance.',
  },
];

export default function WhatIsRudvanSection({ isDarkTheme }) {
  const textColor = isDarkTheme ? 'text-white' : 'text-navy';
  const textMuted = isDarkTheme ? 'text-white/70' : 'text-text-slate';
  const cardBg = isDarkTheme ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200';
  const letterBg = isDarkTheme ? 'bg-brand-blue/20 text-brand-cyan' : 'bg-brand-blue/10 text-brand-blue';

  return (
    <section id="about" className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16 sm:py-16">
      <div className="text-center mb-8 sm:mb-16">
        <h2 className={`font-display text-3xl sm:text-4xl md:text-5xl font-bold mb-4 ${textColor}`}>
          What is <span className="bg-brand-gradient bg-clip-text text-transparent">RUDVAN</span>?
        </h2>
        <p className={`text-lg md:text-xl ${textMuted}`}>An acronym for our adaptive learning technology</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 sm:gap-8">
        <div className={`rounded-card border ${cardBg} p-8`}>
          <h3 className={`text-2xl font-bold ${textColor} mb-6`}>R.U.D.V.A.N</h3>
          <div className="space-y-5">
            {ACRONYM.map(({ letter, word, description }) => (
              <div key={letter} className="flex items-start gap-4">
                <span className={`shrink-0 w-10 h-10 rounded-btn ${letterBg} font-bold flex items-center justify-center`}>
                  {letter}
                </span>
                <div>
                  <p className={`font-bold ${textColor}`}>{word}</p>
                  <p className={`text-sm ${textMuted}`}>{description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 2 columns x 2 rows on mobile instead of a single stacked
            column (explicit user request, docs/DECISIONS.md, "Landing
            page mobile layout" decision) - unchanged at sm:/lg: (2 cols
            tablet, 1 col alongside the acronym card on desktop). Tighter
            gap/padding/text below sm: so 2-up cards stay comfortable on a
            phone width. */}
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-1 gap-3 sm:gap-6">
          {HIGHLIGHT_CARDS.map(({ icon: Icon, title, description }) => (
            <div key={title} className={`rounded-card border ${cardBg} p-3 sm:p-6 flex flex-col sm:flex-row items-start gap-2 sm:gap-4`}>
              <div className="p-2 sm:p-3 rounded-btn bg-brand-gradient shrink-0">
                <Icon className="text-white" size={22} />
              </div>
              <div>
                <p className={`font-bold text-sm sm:text-base ${textColor} mb-1`}>{title}</p>
                <p className={`text-xs sm:text-sm ${textMuted} leading-relaxed`}>{description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
